import { uiPaper, uiSurface } from "shared/ui/uiSurface"
import { Preview, ViewColumn, VisibilityOutlined } from "@mui/icons-material"
import {
  Chip,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
  Box,
  Divider,
} from "@mui/material"
import {
  DataGrid,
  GridColDef,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridRowSelectionModel,
  GridToolbarContainer,
  GridToolbarColumnsButton,
} from "@mui/x-data-grid"
import { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { DashboardOrder } from "../../apis/appApi"
import { TagChip } from "../TagChip"

const formatState = (state: string) =>
  state
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase())

type OrdersGridProps = {
  orders: DashboardOrder[]
  loading: boolean
  checkboxSelection: boolean
  selectedOrderIds: number[]
  onSelectionChange: (ids: number[]) => void
  rowCount: number
  paginationModel: GridPaginationModel
  onPaginationModelChange: (model: GridPaginationModel) => void
}

export const OrdersGrid: FC<OrdersGridProps> = ({
  orders,
  loading,
  checkboxSelection,
  selectedOrderIds,
  onSelectionChange,
  rowCount,
  paginationModel,
  onPaginationModelChange,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // F024: Column visibility — persisted to localStorage
  const STORAGE_KEY = "delivery-orders-column-visibility"
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(() => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY)
        return saved ? JSON.parse(saved) : {}
      } catch {
        return {}
      }
    })

  const handleColumnVisibilityChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      setColumnVisibilityModel(model)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(model))
    },
    [],
  )

  // F026: Quick view popover
  const [quickViewAnchor, setQuickViewAnchor] =
    useState<HTMLElement | null>(null)
  const [quickViewOrder, setQuickViewOrder] = useState<DashboardOrder | null>(
    null,
  )

  const handleQuickView = useCallback(
    (e: React.MouseEvent<HTMLElement>, order: DashboardOrder) => {
      e.stopPropagation()
      setQuickViewAnchor(e.currentTarget)
      setQuickViewOrder(order)
    },
    [],
  )

  const handleOpenDetail = useCallback(
    (orderId: number) => {
      navigate(`/orders/${orderId}`)
    },
    [navigate],
  )

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "reference",
        headerName: t("orders.reference"),
        flex: 1,
        minWidth: 120,
        renderCell: (params) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <span>{params.value}</span>
            <Chip
              label={params.row.stateMachineWorkflow}
              size="small"
              variant="filled"
              color={
                params.row.stateMachineWorkflow === "DELIVERY"
                  ? "primary"
                  : "secondary"
              }
              sx={{ height: 20, fontSize: 11 }}
            />
          </Stack>
        ),
      },
      {
        field: "client",
        headerName: t("ordersPage.columns.client"),
        width: 180,
        valueGetter: (_value, row) => {
          const c = row.client
          return c ? `${c.lastName} ${c.firstName}` : ""
        },
      },
      {
        field: "tags",
        headerName: t("ordersPage.columns.tags"),
        width: 220,
        sortable: false,
        renderCell: (params) => {
          const tags: any[] = params.value ?? []
          if (tags.length === 0) return null
          return (
            <Stack direction="row" spacing={0.5} sx={{ overflow: "hidden", height: "100%", alignItems: "center" }}>
              {tags.slice(0, 3).map((jt: any) => (
                <TagChip key={jt.tagId} tag={jt.tag || {}} size="small" />
              ))}
              {tags.length > 3 && (
                <Chip
                  label={`+${tags.length - 3}`}
                  size="small"
                  sx={{ height: 22, fontSize: 11, bgcolor: "grey.200" }}
                />
              )}
            </Stack>
          )
        },
      },
      {
        field: "address",
        headerName: t("ordersPage.columns.address"),
        flex: 1,
        minWidth: 200,
        valueGetter: (_value, row) => {
          const a = row.address
          if (!a) return ""
          return [a.streetNumber, a.streetName, a.city]
            .filter(Boolean)
            .join(" ")
        },
      },
      {
        field: "amount",
        headerName: t("orders.amount"),
        width: 100,
        renderCell: (params) =>
          params.value != null ? `$${params.value}` : "\u2014",
      },
      {
        field: "state",
        headerName: t("orders.state"),
        width: 180,
        renderCell: (params) => (
          <Chip
            label={formatState(params.value)}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: 11 }}
          />
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 90,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Stack direction="row" spacing={0}>
            <Tooltip title={t("ordersPage.quickView")}>
              <IconButton
                size="small"
                onClick={(e) => handleQuickView(e, params.row as DashboardOrder)}
              >
                <Preview fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("ordersPage.viewDetail")}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  handleOpenDetail(params.row.id as number)
                }}
              >
                <VisibilityOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [t, handleOpenDetail],
  )

  const handleSelectionChange = useCallback(
    (model: GridRowSelectionModel) => {
      onSelectionChange(Array.from(model.ids).map((id) => Number(id)))
    },
    [onSelectionChange],
  )

  return (
    <>
      <DataGrid
        {...uiSurface("UI-DLV2-025")}
        data-testid="orders-grid"
        rows={orders}
        columns={columns}
        loading={loading}
        checkboxSelection={checkboxSelection}
        keepNonExistentRowsSelected={false}
        rowSelectionModel={{
          type: "include" as const,
          ids: new Set(selectedOrderIds),
        }}
        onRowSelectionModelChange={handleSelectionChange}
        onRowClick={(params) => handleOpenDetail(params.row.id as number)}
        paginationMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[25, 50, 100, 200]}
        disableRowSelectionOnClick={false}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityChange}
        slots={{ toolbar: GridToolbarColumnsButton as any }}
        sx={{
          border: 0,
          "& .MuiDataGrid-row": { cursor: "pointer" },
          "& .MuiDataGrid-row.Mui-selected": {
            bgcolor: "primary.50",
            "&:hover": { bgcolor: "primary.100" },
          },
          height: "100%",
        }}
      />

      {/* F026: Quick view popover */}
      <Popover
        slotProps={{ paper: uiPaper("UI-DLV2-026") }}
        open={!!quickViewAnchor}
        anchorEl={quickViewAnchor}
        onClose={() => {
          setQuickViewAnchor(null)
          setQuickViewOrder(null)
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {quickViewOrder && (
          <Box sx={{ p: 2, minWidth: 280, maxWidth: 360 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {quickViewOrder.reference}
            </Typography>
            <Chip
              label={formatState(quickViewOrder.state)}
              size="small"
              variant="outlined"
              sx={{ mt: 0.5, mb: 1 }}
            />
            <Divider sx={{ my: 1 }} />
            {quickViewOrder.client && (
              <Typography variant="body2" color="text.secondary">
                {t("ordersPage.columns.client")}:{" "}
                <strong>
                  {quickViewOrder.client.lastName}{" "}
                  {quickViewOrder.client.firstName}
                </strong>
              </Typography>
            )}
            {quickViewOrder.address && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t("ordersPage.columns.address")}:{" "}
                {[
                  quickViewOrder.address.streetNumber,
                  quickViewOrder.address.streetName,
                  quickViewOrder.address.city,
                ]
                  .filter(Boolean)
                  .join(" ")}
              </Typography>
            )}
            {quickViewOrder.amount != null && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t("orders.amount")}: <strong>${quickViewOrder.amount}</strong>
              </Typography>
            )}
            {quickViewOrder.tags && quickViewOrder.tags.length > 0 && (
              <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                {quickViewOrder.tags.slice(0, 5).map((jt: any) => (
                  <TagChip key={jt.tagId} tag={jt.tag || {}} size="small" />
                ))}
              </Stack>
            )}
          </Box>
        )}
      </Popover>
    </>
  )
}
